import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Pause, Square, RefreshCw, Save, Download, Upload, FolderOpen, File, 
  Plus, Trash2, Edit3, Eye, Code2, Terminal, MessageSquare, Settings,
  ChevronRight, ChevronDown, Search, Command, Zap, Brain, Cpu, Globe,
  GitBranch, Database, Shield, Rocket, BarChart3, Bot, Activity, CheckCircle,
  Clock, AlertTriangle, Maximize2, Minimize2, Split, Copy, ExternalLink, ArrowLeft, Home, X
} from 'lucide-react';
import Editor from '@monaco-editor/react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useCredits } from './hooks/useCredits';
import CreditsManager from './CreditsManager';
import axios from 'axios';
import { Link, useLocation, useParams } from "react-router-dom";

// AI Model configurations shared across components
const claudeModelSettings = {
  'claude-3-5-haiku-20241022': { 
    name: 'Claude 3.5 Haiku', 
    multiplier: 0.4,  // Updated to match backend
    description: 'Fast and efficient for simple tasks',
    badge: 'FAST'
  },
  'claude-3-5-sonnet-20241022': { 
    name: 'Claude 3.5 Sonnet', 
    multiplier: 0.8,  // Updated to match backend
    description: 'Latest Sonnet with improved reasoning',
    badge: 'IMPROVED'
  },
  'claude-3-7-sonnet': { 
    name: 'Claude 3.7 Sonnet', 
    multiplier: 1.0,  // Updated to match backend
    description: 'Advanced model with hybrid reasoning',
    badge: 'ADVANCED'
  },
  'claude-4-sonnet': { 
    name: 'Claude 4 Sonnet', 
    multiplier: 1.2,  // Updated to match backend
    description: 'Superior coding and reasoning capabilities',
    badge: 'LATEST'
  },
  'claude-3-opus': { 
    name: 'Claude 3 Opus', 
    multiplier: 1.4,  // Updated to match backend
    description: 'Most powerful Claude 3 model',
    badge: 'PREMIUM'
  },
  'claude-4-opus': { 
    name: 'Claude 4 Opus', 
    multiplier: 1.6,  // Updated to match backend
    description: 'Best coding model with sustained performance',
    badge: 'ULTIMATE'
  }
};

// Auto-detect backend URL based on current environment
const getBackendURL = () => {
  // TEMPORARY HARDCODE FIX - Force correct backend URL
  const correctBackendUrl = 'https://6c1f0f70-88de-468f-b801-d4c2bfd41f7b.preview.emergentagent.com';
  console.log('🚨 HARDCODED BACKEND URL (temporary fix):', correctBackendUrl);
  return correctBackendUrl;
  
  // FORCE use of environment variable if set (CRITICAL FIX)
  const envBackendUrl = process.env.REACT_APP_BACKEND_URL;
  if (envBackendUrl && envBackendUrl.trim() !== '') {
    console.log('🔧 USING ENVIRONMENT VARIABLE backend URL:', envBackendUrl);
    return envBackendUrl.trim();
  }
  
  console.log('❌ No environment variable found, using auto-detection');
  
  // Get current location info
  const currentDomain = window.location.hostname;
  const currentProtocol = window.location.protocol;
  const currentPort = window.location.port;
  
  console.log('🔍 Auto-detecting backend URL for domain:', currentDomain);
  
  // Production domain mapping
  if (currentDomain === 'tokenos.ai' || currentDomain === 'www.tokenos.ai') {
    console.log('🎯 Production domain detected - using HTTPS');
    return 'https://tokenos.ai';
  }
  
  // Development/localhost
  if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
    const frontendPort = currentPort || '3000';
    const backendPort = frontendPort === '3000' ? '8001' : '8001'; // Always use 8001 for backend
    const backendUrl = `${currentProtocol}//${currentDomain}:${backendPort}`;
    console.log('🧪 Development environment detected - backend URL:', backendUrl);
    return backendUrl;
  }
  
  // Preview/staging domains (Emergent platform)
  if (currentDomain.includes('.preview.emergentagent.com') || 
      currentDomain.includes('.emergentagent.com')) {
    const backendUrl = `${currentProtocol}//${currentDomain}${currentPort ? ':' + currentPort : ''}`;
    console.log('🏗️ Staging/preview environment detected - backend URL:', backendUrl);
    return backendUrl;
  }
  
  // Final fallback: use same domain with HTTPS if possible
  const protocol = currentProtocol === 'https:' ? 'https:' : 'https:'; // Prefer HTTPS
  const backendUrl = `${protocol}//${currentDomain}${currentPort ? ':' + currentPort : ''}`;
  console.log('🔄 Using current domain as fallback - backend URL:', backendUrl);
  return backendUrl;
};

const BACKEND_URL = getBackendURL();
console.log('🚀 TokenOS Frontend - Auto-detected backend URL:', BACKEND_URL);

// Always append /api to the backend URL since all our endpoints use this prefix
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;
console.log('🚀 TokenOS Frontend - Final API URL:', API);

const DevelopmentWorkspace = ({ publicKey, connected }) => {
  const location = useLocation();
  const { projectId } = useParams(); // Get projectId from URL parameters
  
  // Core state
  const [activeFile, setActiveFile] = useState(null);
  const [files, setFiles] = useState({});
  const [fileTree, setFileTree] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(true);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isTerminalOpen, setIsTerminalOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [selectedModel, setSelectedModel] = useState('claude-3-5-sonnet-20241022'); // AI Model selection
  
  // AI and project state
  const [currentProject, setCurrentProject] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);

  // Use shared credits hook
  const { credits: userCredits, refreshCredits } = useCredits();
  
  
  // UI state
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [previewWidth, setPreviewWidth] = useState(400);
  const [terminalHeight, setTerminalHeight] = useState(200);
  const [layout, setLayout] = useState('split'); // 'split', 'editor', 'preview'
  
  // Refs
  const editorRef = useRef(null);
  const chatRef = useRef(null);

  // Initialize project when component mounts
  useEffect(() => {
    if (connected && publicKey) {
      if (projectId) {
        // Load specific project by ID from URL
        loadProjectFromId(projectId);
      } else {
        // Initialize with default or state-based project
        initializeProject();
        initializeChat();
      }
    }
  }, [connected, publicKey, projectId]);

  // Load specific project from backend by ID
  const loadProjectFromId = async (id) => {
    try {
      console.log('🔍 Loading project from ID:', id);
      const response = await axios.get(`${API}/project/${id}`);
      
      if (response.data) {
        const project = response.data;
        console.log('✅ Project loaded:', project);
        
        // Set the current project
        setCurrentProject(project);
        
        // Parse and set the project files
        if (project.generated_code) {
          const parsedFiles = {};
          
          // If generated_code is a string (old format)
          if (typeof project.generated_code === 'string') {
            parsedFiles['src/App.js'] = {
              content: project.generated_code,
              type: 'file',
              language: 'javascript',
              modified: false
            };
          } 
          // If generated_code is an object (new format with multiple files)
          else if (typeof project.generated_code === 'object') {
            Object.keys(project.generated_code).forEach(fileName => {
              const file = project.generated_code[fileName];
              parsedFiles[fileName] = {
                content: file.content || file,
                type: 'file',
                language: getFileLanguage(fileName),
                modified: false
              };
            });
          }
          
          // Ensure we have at least a basic structure
          if (Object.keys(parsedFiles).length === 0) {
            parsedFiles['src/App.js'] = {
              content: '// Project loaded from TokenOS\nexport default function App() {\n  return <div>Hello from TokenOS!</div>;\n}',
              type: 'file',
              language: 'javascript',
              modified: false
            };
          }
          
          setFiles(parsedFiles);
          
          // Create file tree
          const tree = Object.keys(parsedFiles).map(filename => ({
            id: filename,
            name: filename,
            type: 'file',
            language: parsedFiles[filename].language,
            children: []
          }));
          setFileTree(tree);
          
          // Set the first file as active
          const firstFile = Object.keys(parsedFiles)[0];
          if (firstFile) {
            setActiveFile(firstFile);
          }
        }
        
        // Initialize chat with project context
        setChatMessages([{
          type: 'ai',
          content: `Project "${project.prompt || 'Untitled'}" loaded successfully! I'm ready to help you continue developing this project. What would you like to work on?`,
          timestamp: new Date()
        }]);

        // Set preview URL if available
        if (project.preview_url) {
          setPreviewUrl(project.preview_url);
        }
      }
    } catch (error) {
      console.error('❌ Failed to load project:', error);
      setChatMessages([{
        type: 'ai',
        content: 'Sorry, I couldn\'t load that project. It might not exist or you might not have access to it. Loading default project instead.',
        timestamp: new Date()
      }]);
      // Fall back to default project
      initializeProject();
      initializeChat();
    }
  };

  // Helper function to determine file language based on extension
  const getFileLanguage = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const languageMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'json': 'json',
      'md': 'markdown',
      'py': 'python',
      'sol': 'solidity'
    };
    return languageMap[extension] || 'plaintext';
  };

  // Default project structure - Enhanced with more realistic project files
  const defaultProject = {
    id: 'default',
    name: 'TokenOS Web3 Project',
    type: 'dapp',
    files: {
      'README.md': {
        content: `# TokenOS Web3 Project

This project was generated using TokenOS - AI-powered Web3 development platform.

## Features

- 🔗 Solana wallet integration
- 💰 Token operations
- 🎨 Modern React UI with Tailwind CSS
- 🚀 Ready for deployment

## Getting Started

1. Install dependencies: \`npm install\`
2. Start development server: \`npm start\`
3. Connect your Solana wallet
4. Start building amazing Web3 applications!

## TokenOS Platform

Visit [TokenOS](https://tokenos.ai) for more AI-powered Web3 development tools.

## Support

Join our community:
- Telegram: https://t.me/tokenos_ai
- Twitter: https://x.com/TokenOS_AI
`,
        language: 'markdown',
        modified: false
      },
      'src/App.js': {
        content: `import React, { useState, useEffect } from 'react';
import { Connection, PublicKey, clusterApiUrl } from '@solana/web3.js';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { ConnectionProvider, WalletProvider, useWallet } from '@solana/wallet-adapter-react';
import { WalletModalProvider, WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { PhantomWalletAdapter, SolflareWalletAdapter } from '@solana/wallet-adapter-wallets';
import './App.css';

// Import wallet adapter CSS
require('@solana/wallet-adapter-react-ui/styles.css');

const network = WalletAdapterNetwork.Devnet;
const endpoint = clusterApiUrl(network);

const wallets = [
  new PhantomWalletAdapter(),
  new SolflareWalletAdapter({ network }),
];

function WalletConnectionProvider({ children }) {
  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          {children}
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
}

function AppContent() {
  const { publicKey, connected, disconnect } = useWallet();
  const [balance, setBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchBalance = async () => {
    if (!publicKey) return;
    
    setIsLoading(true);
    try {
      const connection = new Connection(endpoint);
      const balance = await connection.getBalance(publicKey);
      setBalance(balance / 1e9); // Convert lamports to SOL
    } catch (error) {
      console.error('Error fetching balance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (connected) {
      fetchBalance();
    }
  }, [connected, publicKey]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-violet-400 bg-clip-text text-transparent">
            TokenOS Web3 DApp
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Built with AI-powered development tools from TokenOS
          </p>
        </div>

        {/* Wallet Connection */}
        <div className="max-w-md mx-auto mb-8">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-2xl font-semibold text-white mb-4 text-center">
              Wallet Connection
            </h2>
            
            <div className="flex justify-center mb-4">
              <WalletMultiButton className="!bg-purple-600 hover:!bg-purple-700" />
            </div>

            {connected && (
              <div className="text-center space-y-3">
                <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg">
                  <p className="text-green-400 font-medium">✅ Wallet Connected!</p>
                </div>
                
                <div className="space-y-2 text-sm">
                  <p className="text-gray-300">
                    <span className="font-medium">Address:</span><br/>
                    <span className="font-mono text-xs break-all">
                      {publicKey?.toString()}
                    </span>
                  </p>
                  
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-300">Balance:</span>
                    <span className="text-white font-semibold">
                      {isLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        \`\${balance.toFixed(4)} SOL\`
                      )}
                    </span>
                  </div>
                </div>

                <button
                  onClick={fetchBalance}
                  className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Refresh Balance
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        {connected && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold text-white mb-3">💰 Token Operations</h3>
              <p className="text-gray-300 text-sm mb-4">
                Send, receive, and manage your SPL tokens with ease.
              </p>
              <button className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
                Coming Soon
              </button>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold text-white mb-3">🎨 NFT Gallery</h3>
              <p className="text-gray-300 text-sm mb-4">
                View and manage your NFT collection on Solana.
              </p>
              <button className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
                Coming Soon
              </button>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold text-white mb-3">📊 DeFi Dashboard</h3>
              <p className="text-gray-300 text-sm mb-4">
                Track your DeFi positions and yield farming rewards.
              </p>
              <button className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
                Coming Soon
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-white/20">
          <p className="text-gray-400 text-sm">
            Powered by{' '}
            <a 
              href="https://tokenos.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 transition-colors"
            >
              TokenOS
            </a>
            {' '}- AI-powered Web3 development platform
          </p>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <WalletConnectionProvider>
      <AppContent />
    </WalletConnectionProvider>
  );
}

export default App;`,
        language: 'javascript',
        modified: false
      },
      'src/App.css': {
        content: `@tailwind base;
@tailwind components;
@tailwind utilities;

.App {
  text-align: center;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Custom button styles */
button {
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

button:hover {
  transform: translateY(-1px);
}

button:active {
  transform: translateY(0);
}

/* Gradient text utility */
.gradient-text {
  background: linear-gradient(45deg, #9333ea, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Glass morphism effect */
.glass {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(147, 51, 234, 0.3);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(147, 51, 234, 0.5);
}

/* Animation utilities */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

/* Responsive design */
@media (max-width: 768px) {
  .container {
    padding: 0 0.5rem;
  }
  
  h1 {
    font-size: 2.5rem;
  }
}`,
        language: 'css',
        modified: false
      },
      'src/index.js': {
        content: `import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);`,
        language: 'javascript',
        modified: false
      },
      'src/index.css': {
        content: `@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';

body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: #000;
  color: #fff;
}

code {
  font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
    monospace;
}

* {
  box-sizing: border-box;
}`,
        language: 'css',
        modified: false
      },
      'package.json': {
        content: `{
  "name": "tokenos-web3-dapp",
  "version": "1.0.0",
  "description": "A Web3 DApp built with TokenOS AI-powered development platform",
  "main": "src/index.js",
  "dependencies": {
    "react": "^18.2.0",
    "react-dom": "^18.2.0",
    "react-scripts": "5.0.1",
    "@solana/web3.js": "^1.87.0",
    "@solana/wallet-adapter-base": "^0.9.23",
    "@solana/wallet-adapter-react": "^0.15.35",
    "@solana/wallet-adapter-react-ui": "^0.9.34",
    "@solana/wallet-adapter-wallets": "^0.19.24",
    "tailwindcss": "^3.3.0",
    "autoprefixer": "^10.4.14",
    "postcss": "^8.4.24"
  },
  "scripts": {
    "start": "react-scripts start",
    "build": "react-scripts build",
    "test": "react-scripts test",
    "eject": "react-scripts eject",
    "deploy": "npm run build && echo 'Ready for deployment!'"
  },
  "keywords": [
    "web3",
    "solana",
    "dapp",
    "tokenos",
    "blockchain",
    "cryptocurrency",
    "defi"
  ],
  "author": "TokenOS User",
  "license": "MIT",
  "browserslist": {
    "production": [
      ">0.2%",
      "not dead",
      "not op_mini all"
    ],
    "development": [
      "last 1 chrome version",
      "last 1 firefox version",
      "last 1 safari version"
    ]
  }
}`,
        language: 'json',
        modified: false
      },
      'tailwind.config.js': {
        content: `/** @type {import('tailwindcss').Config} */
module.exports = {
  content: [
    "./src/**/*.{js,jsx,ts,tsx}",
    "./public/index.html"
  ],
  theme: {
    extend: {
      colors: {
        'tokenos': {
          'purple': '#9333ea',
          'violet': '#7c3aed'
        }
      },
      fontFamily: {
        'mono': ['source-code-pro', 'Menlo', 'Monaco', 'Consolas', 'Courier New', 'monospace']
      },
      animation: {
        'fade-in': 'fadeIn 0.3s ease-in-out',
        'bounce-slow': 'bounce 2s infinite'
      }
    },
  },
  plugins: [],
}`,
        language: 'javascript',
        modified: false
      }
    }
  };


  const initializeProject = () => {
    // Check if project data was passed from Edit Code button
    const passedProject = location.state?.project;
    
    let project;
    if (passedProject) {
      // Use the project data from Edit Code
      console.log('🔄 Loading project from Edit Code:', passedProject);
      
      // Extract and parse the code
      let extractedCode = '';
      
      console.log('🔍 Available project fields:', Object.keys(passedProject));
      
      // Try to get code from different possible fields in priority order
console.log("🔍 Available project fields:", Object.keys(passedProject));
      if (passedProject.generated_code) {
        extractedCode = passedProject.generated_code;
        console.log('✅ Found code in generated_code field');
      } else if (passedProject.code) {
        extractedCode = passedProject.code;
        console.log('✅ Found code in code field');
      } else if (passedProject.fullResponse) {
        extractedCode = passedProject.fullResponse;
        console.log('✅ Found code in fullResponse field');
      } else if (passedProject.response) {
        extractedCode = passedProject.response;
        console.log('✅ Found code in response field');
      } else {
        // Fallback to default code if none found
        extractedCode = `// TokenOS Project: ${passedProject.title || 'Generated Project'}
// Description: ${passedProject.description || 'AI-generated project from TokenOS'}

import React, { useState } from 'react';

function App() {
  const [message, setMessage] = useState('Welcome to your TokenOS project!');

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#1a1a1a',
      color: 'white'
    }}>
      <div style={{
        padding: '2rem',
        backgroundColor: '#2a2a2a',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        textAlign: 'center',
        maxWidth: '500px'
      }}>
        <h1 style={{ color: '#9333ea', marginBottom: '1rem' }}>
          🚀 ${passedProject.title || 'TokenOS Project'}
        </h1>
        <p style={{ marginBottom: '1.5rem', color: '#ccc' }}>
          {message}
        </p>
        <button 
          onClick={() => setMessage('Your project is working perfectly!')}
          style={{
            padding: '12px 24px',
            backgroundColor: '#9333ea',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#7c3aed'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#9333ea'}
        >
          Test Your Project
        </button>
        <div style={{ 
          marginTop: '1.5rem', 
          padding: '1rem', 
          backgroundColor: '#1a1a1a', 
          borderRadius: '4px',
          border: '1px solid #333'
        }}>
          <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>
            💡 Edit this code in the Development Workspace to customize your project
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;`;
      }
      
      // Create files object from the extracted code
      const projectFiles = {
        'src/App.js': {
          content: extractedCode,
          language: 'javascript',
          modified: false
        },
        'README.md': {
          content: `# ${passedProject.title || 'TokenOS Project'}

${passedProject.description || 'This project was built using TokenOS AI-powered development platform.'}

## Features

- 🚀 Built with React and modern Web3 technologies
- 🎨 Responsive design with Tailwind CSS
- 🔗 Ready for deployment
- 🤖 AI-generated code optimized for performance

## Project Details

- **Created**: ${new Date(passedProject.created_at || Date.now()).toLocaleDateString()}
- **Status**: ${passedProject.status || 'Active'}
- **Type**: Web3 Application

## Getting Started

1. Install dependencies: \`npm install\`
2. Start development server: \`npm start\`
3. Start building amazing applications!

## TokenOS Platform

Visit [TokenOS](https://tokenos.ai) for more AI-powered development tools.

## Code Structure

The main application code is in \`src/App.js\`. This file contains:

\`\`\`javascript
${extractedCode.slice(0, 300)}...
\`\`\`

Feel free to modify and extend this code to fit your needs!`,
          language: 'markdown',
          modified: false
        }
      };
      
      // Add additional files from default project
      Object.keys(defaultProject.files).forEach(filename => {
        if (!projectFiles[filename] && filename !== 'src/App.js' && filename !== 'README.md') {
          projectFiles[filename] = defaultProject.files[filename];
        }
      });
      
      project = {
        id: passedProject.id || 'imported',
        name: passedProject.title || 'Imported Project',
        type: 'dapp',
        files: projectFiles
      };
      
      console.log('✅ Project loaded successfully with code from Edit Code button');
      console.log('📝 Code preview:', extractedCode.slice(0, 200) + '...');
    } else {
      // Use default project if no project data passed
      console.log('🔄 Loading default project');
      project = defaultProject;
    }
    
    setCurrentProject(project);
    setFiles(project.files);
    
    // Create file tree
    const tree = Object.keys(project.files).map(filename => ({
      id: filename,
      name: filename,
      type: 'file',
      language: project.files[filename].language,
      children: []
    }));
    setFileTree(tree);
    
    // Set first file as active
    const firstFile = Object.keys(project.files)[0];
    setActiveFile(firstFile);
    
    // Set preview URL if available
    if (passedProject && passedProject.preview_url) {
      setPreviewUrl(passedProject.preview_url);
    } else {
      // Generate initial preview
      generatePreview();
    }
  };

  const initializeChat = () => {
    setChatMessages([
      {
        id: 1,
        type: 'ai',
        content: "🚀 **EMERGENT-LEVEL AI Development Assistant** - World-Class Coding Capabilities Activated!\n\n⚡ **ENTERPRISE-GRADE FEATURES:**\n• **Production Code Generation** - Write 2000-5000+ line applications\n• **Real API Integrations** - Connect to live data sources (crypto, DeFi, weather, news)\n• **Advanced Architecture** - Enterprise patterns used by billion-dollar platforms\n• **Performance Optimization** - Apple/Google/Tesla-level code quality\n• **Security Analysis** - Comprehensive vulnerability detection\n• **Business Logic** - Sophisticated calculations and real-world functionality\n\n🔥 **REAL-TIME CAPABILITIES:**\n• **Live Data Integration** - Crypto prices, DeFi protocols, NFT collections\n• **Advanced UI/UX** - Glassmorphism, animations, responsive design\n• **Multi-Platform Support** - Web3, DeFi, E-commerce, SaaS applications\n• **Professional Standards** - Code that rivals industry leaders\n\n💎 **EMERGENT SPECIALIZATIONS:**\n• **Crypto/Web3** - DeFi protocols, trading platforms, token launches\n• **E-commerce** - Full marketplace with payments and logistics\n• **SaaS Platforms** - Enterprise collaboration and productivity tools\n• **Analytics** - Real-time dashboards with live data feeds\n\n🎯 **WHAT I CAN BUILD FOR YOU:**\n• Complete DeFi trading platforms with live charts\n• E-commerce marketplaces with payment processing\n• Professional dashboards with real-time analytics\n• Social platforms with advanced user management\n• Any enterprise-grade application you can imagine\n\nI have access to **REAL APIs** and **LIVE DATA** - no more mock data! What world-class application would you like to create?",
        timestamp: new Date()
      }
    ]);
  };

  const generatePreview = async () => {
    try {
      if (!currentProject || !files['src/App.js']) return;
      
      // Create a more realistic preview
      const projectId = currentProject.id;
      
      // For demo, simulate a deployment URL
      setTimeout(() => {
        const mockUrl = `https://preview.tokenos.ai/project/${projectId}`;
        setPreviewUrl(mockUrl);
      }, 1000);
      
      console.log('Generating live preview for project:', projectId);
    } catch (error) {
      console.error('Failed to generate preview:', error);
    }
  };

  const handleEditorChange = (value, event) => {
    if (!activeFile) return;
    
    setFiles(prev => ({
      ...prev,
      [activeFile]: {
        ...prev[activeFile],
        content: value,
        modified: true
      }
    }));
    
    // Auto-generate AI suggestions
    if (value && value.length > 100) {
      generateAdvancedSuggestions(value, chatInput);
    }
  };

  const generateAdvancedSuggestions = async (code, userContext) => {
    try {
      // EMERGENT-LEVEL AI SUGGESTIONS with real-time data integration
      const suggestions = [];
      
      // Analyze code for potential real API integrations
      if (code.includes('crypto') || code.includes('token') || code.includes('price')) {
        suggestions.push({
          type: 'integration',
          title: 'Live Crypto Data Integration',
          description: 'Integrate real-time cryptocurrency prices using CoinGecko API',
          code: `// Add live crypto price integration
const fetchCryptoPrices = async () => {
  try {
    const response = await fetch('/api/real-data/crypto-prices?symbols=BTC,ETH,SOL');
    const data = await response.json();
    return data.success ? data.data : data.fallback_data;
  } catch (error) {
    console.error('Failed to fetch crypto prices:', error);
  }
};`,
          impact: 'Replaces mock data with live market prices'
        });
      }
      
      if (code.includes('defi') || code.includes('yield') || code.includes('tvl')) {
        suggestions.push({
          type: 'integration',
          title: 'DeFi Protocol Data',
          description: 'Connect to real DeFi protocol analytics via DefiLlama',
          code: `// Add DeFi protocol data integration
const fetchDeFiProtocols = async () => {
  try {
    const response = await fetch('/api/real-data/defi-protocols?limit=20');
    const data = await response.json();
    return data.success ? data.data : data.fallback_data;
  } catch (error) {
    console.error('Failed to fetch DeFi data:', error);
  }
};`,
          impact: 'Access real TVL, APY, and protocol metrics'
        });
      }
      
      if (code.includes('nft') || code.includes('collection') || code.includes('marketplace')) {
        suggestions.push({
          type: 'integration',
          title: 'NFT Collections Data',
          description: 'Integrate live NFT marketplace data from OpenSea',
          code: `// Add NFT collections data integration
const fetchNFTCollections = async () => {
  try {
    const response = await fetch('/api/real-data/nft-collections');
    const data = await response.json();
    return data.success ? data.data : data.fallback_data;
  } catch (error) {
    console.error('Failed to fetch NFT data:', error);
  }
};`,
          impact: 'Display real floor prices, volumes, and collection stats'
        });
      }
      
      // Performance optimization suggestions
      if (code.length > 1000) {
        suggestions.push({
          type: 'optimization',
          title: 'Performance Optimization',
          description: 'Implement React.memo and useMemo for better performance',
          code: `// Optimize component performance
const OptimizedComponent = React.memo(({ data }) => {
  const expensiveValue = React.useMemo(() => {
    return data.reduce((acc, item) => acc + item.value, 0);
  }, [data]);
  
  return <div>{expensiveValue}</div>;
});`,
          impact: 'Reduces unnecessary re-renders and improves performance'
        });
      }
      
      // Design enhancement suggestions
      if (code.includes('className') || code.includes('style')) {
        suggestions.push({
          type: 'design',
          title: 'Glassmorphism Enhancement',
          description: 'Apply modern glassmorphism design patterns',
          code: `// Apply glassmorphism styling
const glassStyle = {
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
};`,
          impact: 'Creates modern, professional visual appearance'
        });
      }
      
      // Real-time data suggestions based on user context
      if (userContext?.toLowerCase().includes('real') || userContext?.toLowerCase().includes('live')) {
        suggestions.push({
          type: 'feature',
          title: 'Real-time Data Updates',
          description: 'Add WebSocket-like real-time data updates',
          code: `// Implement real-time data updates
React.useEffect(() => {
  const interval = setInterval(async () => {
    const newData = await fetchLiveData();
    setData(newData);
  }, 10000); // Update every 10 seconds
  
  return () => clearInterval(interval);
}, []);`,
          impact: 'Keeps data fresh with automatic updates'
        });
      }
      
      setAiSuggestions(suggestions);
      
    } catch (error) {
      console.error('Failed to generate advanced suggestions:', error);
      setAiSuggestions([
        {
          type: 'general',
          title: 'EMERGENT-Level Development',
          description: 'Your code is ready for enterprise-grade enhancements',
          code: '// Ask me for specific improvements you\'d like to implement',
          impact: 'Transforms code to production-ready standards'
        }
      ]);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: chatInput,
      timestamp: new Date()
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsAiThinking(true);
    
    try {
      // EMERGENT-LEVEL AI ASSISTANCE with real API integrations
      const enhancedRequest = {
        prompt: `DEVELOPMENT WORKSPACE AI REQUEST:
Context: User is working in Development Workspace on project: "${currentProject?.prompt || 'New Project'}"
Current active file: ${activeFile || 'None'}
User request: "${chatInput}"

PROVIDE EMERGENT-LEVEL ASSISTANCE:
- If code generation: Write production-ready, enterprise-grade code
- If data needed: Integrate with real APIs (crypto, DeFi, weather, news)
- If debugging: Provide sophisticated analysis and solutions
- If optimization: Suggest Apple/Google/Tesla-level improvements
- If architecture: Recommend billion-dollar platform patterns

REAL DATA INTEGRATIONS AVAILABLE:
- /api/real-data/crypto-prices for live cryptocurrency data
- /api/real-data/defi-protocols for DeFi protocol information
- /api/real-data/nft-collections for NFT marketplace data
- /api/real-data/weather for location-based features
- /api/real-data/news for content and updates

CURRENT PROJECT CONTEXT:
Files: ${Object.keys(files).join(', ')}
Active File Content: ${files[activeFile]?.content?.slice(0, 500) || 'No content'}...

Provide sophisticated, production-ready assistance that leverages real data sources.`,
        model: selectedModel,
        user_wallet: publicKey?.toString(),
        workspace_context: {
          currentProject: currentProject,
          activeFile: activeFile,
          fileContent: files[activeFile]?.content || '',
          availableFiles: Object.keys(files)
        }
      };
      
      // Use the enhanced AI generation endpoint
      const response = await axios.post(`${API}/create-project`, enhancedRequest);
      
      let aiResponse = "I'm here to help with your development! What specific assistance do you need?";
      
      if (response.data?.generated_code) {
        // If the AI generated code, offer to apply it
        const generatedCode = response.data.generated_code;
        aiResponse = `🚀 **EMERGENT-LEVEL AI SOLUTION:**\n\n${generatedCode}\n\n💡 **NEXT STEPS:**\n• Review the generated code above\n• I can integrate this into your project files\n• Ask me to explain any part of the implementation\n• Request optimizations or modifications\n\nWould you like me to apply this code to your project?`;
        
        // Store the generated code for potential application
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: aiResponse,
          timestamp: new Date(),
          generatedCode: generatedCode
        }]);
      } else {
        // General AI assistance
        aiResponse = `🎯 **EMERGENT-LEVEL ASSISTANCE:**\n\nI understand you need help with: "${chatInput}"\n\n⚡ **I CAN HELP YOU:**\n• Generate production-ready code with real API integrations\n• Debug and optimize your current implementation\n• Integrate live data from crypto, DeFi, weather, and news APIs\n• Apply enterprise-grade architecture patterns\n• Implement advanced UI/UX with glassmorphism and animations\n\n🔥 **REAL-TIME CAPABILITIES:**\n• Access live cryptocurrency prices via CoinGecko\n• Connect to DeFi protocols via DefiLlama\n• Integrate NFT data from OpenSea\n• Add weather data for location features\n• Include news feeds for dynamic content\n\nPlease be more specific about what you'd like me to build or improve, and I'll create sophisticated, production-ready code for you!`;
        
        setChatMessages(prev => [...prev, {
          id: Date.now() + 1,
          type: 'ai',
          content: aiResponse,
          timestamp: new Date()
        }]);
      }
      
      // Auto-generate AI suggestions based on context
      if (activeFile && files[activeFile]) {
        generateAdvancedSuggestions(files[activeFile].content, chatInput);
      }
      
    } catch (error) {
      console.error('AI Chat Error:', error);
      setChatMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'ai',
        content: `⚠️ **AI ASSISTANCE TEMPORARILY UNAVAILABLE**\n\nError: ${error.message}\n\n💡 **MEANWHILE, I CAN HELP WITH:**\n• Code review and optimization suggestions\n• Architecture recommendations\n• Real-time data integration guidance\n• UI/UX improvement ideas\n\nPlease try your request again, or ask me about specific development topics!`,
        timestamp: new Date()
      }]);
    } finally {
      setIsAiThinking(false);
    }
  };

  const saveProject = async () => {
    if (isSaving) return; // Prevent multiple save attempts
    
    if (!currentProject?.id) {
      alert('❌ No project loaded to save');
      return;
    }
    
    if (!publicKey) {
      alert('❌ Please connect your wallet to save the project');
      return;
    }
    
    setIsSaving(true);
    try {
      const saveData = {
        project_id: currentProject.id,
        files: files,
        user_wallet: publicKey.toString()
      };
      
      // Save to backend using the new save-project endpoint
      await axios.post(`${API}/save-project`, saveData);
      
      // Mark all files as saved
      setFiles(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(filename => {
          updated[filename].modified = false;
        });
        return updated;
      });
      
      alert('✅ Project saved successfully!');
    } catch (error) {
      console.error('Failed to save project:', error);
      const errorMessage = error.response?.data?.detail || error.message;
      alert(`❌ Failed to save project: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  const createNewFile = () => {
    const filename = prompt('Enter filename (e.g., components/Button.js):');
    if (!filename) return;
    
    const extension = filename.split('.').pop();
    const template = getFileTemplate(extension);
    
    setFiles(prev => ({
      ...prev,
      [filename]: {
        content: template,
        language: getLanguageFromFilename(filename),
        modified: false
      }
    }));
    
    setFileTree(prev => [...prev, {
      id: filename,
      name: filename,
      type: 'file',
      language: getLanguageFromFilename(filename),
      children: []
    }]);
    
    setActiveFile(filename);
  };

  const getFileTemplate = (extension) => {
    const templates = {
      'js': `// New JavaScript file
import React from 'react';

const NewComponent = () => {
  return (
    <div>
      <h1>New Component</h1>
    </div>
  );
};

export default NewComponent;`,
      'jsx': `import React from 'react';

const NewComponent = () => {
  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold">New Component</h1>
    </div>
  );
};

export default NewComponent;`,
      'css': `/* New CSS file */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.button {
  background: linear-gradient(45deg, #9333ea, #7c3aed);
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(147, 51, 234, 0.3);
}`,
      'sol': `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.19;

contract NewContract {
    address public owner;
    
    constructor() {
        owner = msg.sender;
    }
    
    modifier onlyOwner() {
        require(msg.sender == owner, "Not the owner");
        _;
    }
    
    function changeOwner(address newOwner) public onlyOwner {
        require(newOwner != address(0), "Invalid address");
        owner = newOwner;
    }
}`,
      'md': `# New Document

This is a new markdown document.

## Features

- Feature 1
- Feature 2
- Feature 3

## Usage

\`\`\`javascript
// Example code
console.log('Hello, TokenOS!');
\`\`\`

## Contributing

1. Fork the repository
2. Create your feature branch
3. Commit your changes
4. Push to the branch
5. Create a Pull Request`
    };
    
    return templates[extension] || '// New file\n';
  };

  const getLanguageFromFilename = (filename) => {
    const ext = filename.split('.').pop();
    const langMap = {
      'js': 'javascript',
      'jsx': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'css': 'css',
      'html': 'html',
      'json': 'json',
      'sol': 'solidity',
      'py': 'python',
      'rs': 'rust',
      'md': 'markdown',
      'yml': 'yaml',
      'yaml': 'yaml'
    };
    return langMap[ext] || 'javascript';
  };

  const deployProject = async () => {
    try {
      setIsAiThinking(true);
      
      // Enhanced deployment with project data
      const response = await axios.post(`${API}/create-project`, {
        prompt: `DEPLOY PROJECT: ${currentProject?.name}
        
Project Files:
${Object.entries(files).map(([name, file]) => `${name}:\n${file.content.slice(0, 200)}...`).join('\n\n')}

Please deploy this project and provide a live URL.`,
        user_wallet: publicKey.toString()
      });
      
      if (response.data.preview_url) {
        setPreviewUrl(response.data.preview_url);
        const deployMessage = {
          id: Date.now(),
          type: 'ai',
          content: `🚀 **Project deployed successfully!**\n\nYour app is now live at: ${response.data.preview_url}\n\n✅ Features included:\n• Responsive design\n• Wallet integration\n• Production optimizations\n• SSL certificate\n• Global CDN\n\n🔗 You can share this URL with others or use it for testing.`,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, deployMessage]);
      } else {
        const processingMessage = {
          id: Date.now(),
          type: 'ai',
          content: `🔄 **Deployment initiated!**\n\nYour project is being deployed with the following features:\n• Production build optimization\n• SSL certificate setup\n• Global CDN distribution\n• Performance monitoring\n\nDeployment typically takes 2-3 minutes. You'll receive the live URL once ready.`,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, processingMessage]);
      }
      
      // Update credits
    } catch (error) {
      console.error('Deploy failed:', error);
      const errorMessage = {
        id: Date.now(),
        type: 'ai',
        content: `❌ **Deployment failed.**\n\nError: ${error.response?.data?.detail || 'Unknown error'}\n\nPlease check your project code and try again. Make sure you have sufficient credits for deployment.`,
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsAiThinking(false);
    }
  };

  if (!connected) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center pt-16">
        <div className="text-center">
          <Cpu className="h-16 w-16 text-purple-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Connect Wallet to Access Development Workspace</h2>
          <p className="text-gray-400">Connect your Solana wallet to start building with AI-powered tools</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col pt-16">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="flex items-center space-x-1 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>
            <div className="flex items-center space-x-2">
              <Code2 className="h-6 w-6 text-purple-500" />
              <div>
                <h1 className="text-lg font-bold">Development Workspace</h1>
                <p className="text-xs text-gray-400">{currentProject?.name || 'Loading...'}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="text-xs text-gray-400">
              Credits: <span className="text-purple-400 font-medium">{userCredits}</span>
            </div>
            <button
              onClick={() => setShowCreditsModal(true)}
              className="flex items-center space-x-1 px-2 py-1 bg-purple-600 hover:bg-purple-700 rounded text-xs transition-colors"
            >
              <Plus className="h-3 w-3" />
              <span>Buy Credits</span>
            </button>
            <button
              onClick={saveProject}
              disabled={isSaving}
              className={`flex items-center space-x-1 px-3 py-1 rounded text-sm transition-colors ${
                isSaving 
                  ? 'bg-blue-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save</span>
                </>
              )}
            </button>
            <button
              onClick={deployProject}
              className="flex items-center space-x-1 px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm transition-colors"
            >
              <Rocket className="h-4 w-4" />
              <span>Deploy</span>
            </button>
            <button
              onClick={() => setLayout(layout === 'split' ? 'editor' : 'split')}
              className="p-1 hover:bg-gray-700 rounded transition-colors"
              title="Toggle layout"
            >
              <Split className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - File Explorer */}
        <div className="w-64 border-r border-gray-800 bg-gray-900/30 flex flex-col">
          <div className="p-3 border-b border-gray-800">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium">Project Files</h3>
              <button
                onClick={createNewFile}
                className="p-1 hover:bg-gray-700 rounded transition-colors"
                title="Create new file"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2">
            {fileTree.map((file) => (
              <button
                key={file.id}
                onClick={() => setActiveFile(file.id)}
                className={`w-full flex items-center space-x-2 p-2 rounded hover:bg-gray-700/50 transition-colors text-left ${
                  activeFile === file.id ? 'bg-purple-600/20 border border-purple-600/50' : ''
                }`}
              >
                <File className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm truncate">{file.name}</span>
                {files[file.id]?.modified && (
                  <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex">
          {/* Code Editor */}
          <div className={`${layout === 'preview' ? 'hidden' : 'flex-1'} flex flex-col min-w-0`}>
            <div className="border-b border-gray-800 px-4 py-2 bg-gray-900/30 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">{activeFile || 'No file selected'}</span>
                {files[activeFile]?.modified && (
                  <span className="text-xs text-orange-400">• Modified</span>
                )}
              </div>
              <div className="text-xs text-gray-400">
                {files[activeFile]?.language || 'text'}
              </div>
            </div>
            
            <div className="flex-1 min-h-0">
              <Editor
                height="100%"
                defaultLanguage={files[activeFile]?.language || 'javascript'}
                language={files[activeFile]?.language || 'javascript'}
                value={files[activeFile]?.content || ''}
                onChange={handleEditorChange}
                theme="vs-dark"
                onMount={(editor) => {
                  editorRef.current = editor;
                }}
                options={{
                  minimap: { enabled: true },
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                  suggest: {
                    showInlineDetails: true,
                    showKeywords: true,
                    showSnippets: true
                  },
                  folding: true,
                  lineNumbers: 'on',
                  renderLineHighlight: 'all'
                }}
              />
            </div>
          </div>

          {/* Preview Panel */}
          {(layout === 'split' || layout === 'preview') && (
            <div className="w-96 border-l border-gray-800 flex flex-col">
              <div className="border-b border-gray-800 px-4 py-2 bg-gray-900/30 flex items-center justify-between">
                <h3 className="text-sm font-medium">Live Preview</h3>
                <div className="flex space-x-1">
                  <button
                    onClick={generatePreview}
                    className="p-1 hover:bg-gray-700 rounded transition-colors"
                    title="Refresh preview"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  {previewUrl && (
                    <button
                      onClick={() => window.open(previewUrl, '_blank')}
                      className="p-1 hover:bg-gray-700 rounded transition-colors"
                      title="Open in new tab"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="flex-1 bg-gray-100">
                {previewUrl ? (
                  <iframe
                    src={previewUrl}
                    className="w-full h-full border-0"
                    title="Live Preview"
                    sandbox="allow-scripts allow-same-origin"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <Globe className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No preview available</p>
                      <button
                        onClick={generatePreview}
                        className="text-purple-400 hover:text-purple-300 text-sm mt-2 underline"
                      >
                        Generate Preview
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* AI Chat Panel */}
        <div className="w-80 border-l border-gray-800 bg-gray-900/30 flex flex-col">
          <div className="p-3 border-b border-gray-800">
            <div className="flex items-center space-x-2">
              <Brain className="h-5 w-5 text-purple-400" />
              <h3 className="text-sm font-medium">AI Assistant</h3>
              <div className="flex-1"></div>
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {chatMessages.map((message) => (
              <div
                key={message.id}
                className={`${
                  message.type === 'user' 
                    ? 'ml-4 bg-purple-600/20 border border-purple-600/50' 
                    : 'mr-4 bg-gray-800/50 border border-gray-700'
                } p-3 rounded-lg`}
              >
                <div className="flex items-start space-x-2">
                  {message.type === 'ai' && (
                    <Bot className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {isAiThinking && (
              <div className="mr-4 bg-gray-800/50 border border-gray-700 p-3 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Bot className="h-4 w-4 text-purple-400" />
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={chatRef}></div>
          </div>
          
          <div className="p-3 border-t border-gray-800">
            {/* AI Model Selector for Development Workspace */}
            {connected && (
              <div className="mb-3 p-2 bg-gray-800/50 rounded border border-gray-700">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-300">AI Model:</span>
                  <select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="bg-gray-800 text-white rounded border border-gray-600 px-2 py-1 text-xs"
                  >
                    {Object.entries(claudeModelSettings).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.name} ({Math.ceil(30 * config.multiplier)} credits)
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-purple-400">
                    Cost: {Math.ceil(30 * claudeModelSettings[selectedModel].multiplier)} credits
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex space-x-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Ask AI for help with your code..."
                className="flex-1 bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={sendChatMessage}
                disabled={!chatInput.trim() || isAiThinking}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors"
              >
                <Zap className="h-4 w-4" />
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Cost: {Math.ceil(5 * claudeModelSettings[selectedModel].multiplier)} credits per message
            </div>
          </div>
        </div>
      </div>

      {/* Credits Manager Modal */}
      {showCreditsModal && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
          <div className="relative">
            <button
              onClick={() => setShowCreditsModal(false)}
              className="absolute -top-2 -right-2 bg-gray-800 hover:bg-gray-700 text-white rounded-full p-1 z-10"
            >
              <X className="h-4 w-4" />
            </button>
            <CreditsManager
              userCredits={userCredits}
              onPurchase={() => {
                refreshCredits();
                setShowCreditsModal(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DevelopmentWorkspace;